import type { CapabilityMapDataset } from "./types";

export const mockDataset: CapabilityMapDataset = {
  // ── Component Capabilities ──────────────────────────────────────────────────
  componentCapabilities: [
    {
      id: "cc_gnss_denied_nav",
      label: "< 0.1° heading accuracy without GPS",
      description: "High-precision heading estimation using INS/magnetometer fusion — no external reference required.",
      enablesSystemCapIds: ["sc_gnss_denied", "sc_terminal_guidance"],
    },
    {
      id: "cc_ins_fusion",
      label: "Multi-sensor INS fusion (IMU + baro + mag)",
      description: "Tight-coupling of IMU, barometer, and magnetometer for drift-bounded dead-reckoning navigation.",
      enablesSystemCapIds: ["sc_gnss_denied", "sc_bvlos"],
    },
    {
      id: "cc_lidar_slam",
      label: "Real-time 3D SLAM via LiDAR",
      description: "Simultaneous localisation and mapping from LiDAR point clouds at >10 Hz update rate.",
      enablesSystemCapIds: ["sc_indoor_nav", "sc_gnss_denied"],
    },
    {
      id: "cc_thermal_5km",
      label: "Thermal imaging at ≥ 5 km slant range",
      description: "MWIR/LWIR detector with ≥ 640×512 resolution capable of vehicle-class target detection at 5+ km.",
      enablesSystemCapIds: ["sc_target_id", "sc_persistent_isr"],
    },
    {
      id: "cc_eo_stabilization",
      label: "< 0.02 mrad angular stabilisation",
      description: "Gyro-stabilised gimbal platform with line-of-sight error below 0.02 mrad for diffraction-limited imagery.",
      enablesSystemCapIds: ["sc_target_id", "sc_sar_collection"],
    },
    {
      id: "cc_sar_imaging",
      label: "Stripmap/spotlight SAR at < 0.3 m resolution",
      description: "X/Ku-band synthetic aperture radar producing sub-0.3 m resolution all-weather imagery.",
      enablesSystemCapIds: ["sc_sar_collection", "sc_persistent_isr"],
    },
    {
      id: "cc_ads_b_daa",
      label: "ADS-B detect-and-avoid (DO-365 compliant)",
      description: "FAA DO-365-compliant ADS-B In/Out transponder with well-clear avoidance logic for BVLOS operations.",
      enablesSystemCapIds: ["sc_bvlos", "sc_cargo_delivery"],
    },
    {
      id: "cc_active_daa",
      label: "Active obstacle detection ≥ 500 m",
      description: "Radar or LiDAR-based active DAA sensor detecting non-cooperative obstacles at ≥ 500 m range.",
      enablesSystemCapIds: ["sc_bvlos", "sc_perimeter_patrol"],
    },
    {
      id: "cc_px4_autopilot",
      label: "Concurrent autonomous waypoint navigation",
      description: "Open-standard flight controller executing multi-waypoint missions with contingency handling.",
      enablesSystemCapIds: ["sc_comms_denied", "sc_bvlos", "sc_cargo_delivery"],
    },
    {
      id: "cc_redundant_comms",
      label: "Dual-link communications fallback",
      description: "Primary + secondary RF link with autonomous mode escalation on link loss.",
      enablesSystemCapIds: ["sc_comms_denied", "sc_perimeter_patrol"],
    },
    {
      id: "cc_mesh_p2p",
      label: "Mesh peer-to-peer communications (< 50 ms)",
      description: "Self-healing RF mesh protocol enabling peer-to-peer data exchange with < 50 ms round-trip latency.",
      enablesSystemCapIds: ["sc_swarm_coord", "sc_comms_denied"],
    },
    {
      id: "cc_swarm_coord",
      label: "Decentralised swarm position sharing",
      description: "Agent-local consensus algorithm broadcasting relative position without a ground control station.",
      enablesSystemCapIds: ["sc_swarm_coord"],
    },
    {
      id: "cc_precision_land",
      label: "Centimetre-precision landing (< 5 cm RMS)",
      description: "Vision-inertial landing system achieving < 5 cm RMS touchdown on unimproved or moving surfaces.",
      enablesSystemCapIds: ["sc_precision_land", "sc_cargo_delivery"],
    },
    {
      id: "cc_winch_deploy",
      label: "Payload winch ± 0.3 m placement accuracy",
      description: "Motorised tether with real-time sway compensation for contactless cargo delivery.",
      enablesSystemCapIds: ["sc_cargo_delivery"],
    },
    {
      id: "cc_efficient_prop",
      label: "Motor efficiency > 85 % across flight envelope",
      description: "High-efficiency brushless motor + ESC combination sustaining > 85 % conversion efficiency at cruise.",
      enablesSystemCapIds: ["sc_bvlos"],
    },
  ],

  // ── Component Types ─────────────────────────────────────────────────────────
  componentTypes: [
    {
      id: "nav_module",
      label: "Navigation Module (INS/IMU)",
      description: "Inertial navigation system or IMU providing attitude, velocity, and position in GPS-denied conditions.",
      levelHint: "subsystem",
      componentCapabilityIds: ["cc_gnss_denied_nav", "cc_ins_fusion"],
    },
    {
      id: "eo_ir_gimbal",
      label: "EO/IR Gimbal",
      description: "Electro-optical / infrared stabilised camera turret for day/night ISR and targeting.",
      levelHint: "subsystem",
      componentCapabilityIds: ["cc_thermal_5km", "cc_eo_stabilization"],
    },
    {
      id: "sar_payload",
      label: "SAR Payload",
      description: "Synthetic aperture radar module for all-weather, day/night ground imaging.",
      levelHint: "subsystem",
      componentCapabilityIds: ["cc_sar_imaging"],
    },
    {
      id: "daa_system",
      label: "Detect-and-Avoid System",
      description: "Integrated sensor + logic module that detects and avoids cooperative and non-cooperative airspace hazards.",
      levelHint: "subsystem",
      componentCapabilityIds: ["cc_ads_b_daa", "cc_active_daa"],
    },
    {
      id: "flight_controller",
      label: "Flight Controller",
      description: "Onboard autopilot executing guidance, navigation, and control with autonomous mission management.",
      levelHint: "component",
      componentCapabilityIds: ["cc_px4_autopilot", "cc_redundant_comms"],
    },
    {
      id: "mesh_radio",
      label: "Mesh Radio Module",
      description: "Software-defined radio module enabling multi-node mesh networking for swarm coordination.",
      levelHint: "component",
      componentCapabilityIds: ["cc_mesh_p2p", "cc_swarm_coord"],
    },
    {
      id: "precision_landing_sys",
      label: "Precision Landing System",
      description: "Vision-based terminal guidance module for precision landing and hover-winch delivery.",
      levelHint: "component",
      componentCapabilityIds: ["cc_precision_land", "cc_winch_deploy"],
    },
    {
      id: "propulsion_system",
      label: "Propulsion System",
      description: "Brushless motor, ESC, and propeller assembly optimised for endurance at cruise.",
      levelHint: "component",
      componentCapabilityIds: ["cc_efficient_prop"],
    },
  ],

  // ── System Capabilities ──────────────────────────────────────────────────────
  systemCapabilities: [
    {
      id: "sc_gnss_denied",
      label: "Navigate in GNSS-denied environments",
      description: "Maintain accurate positioning and autonomous flight when GPS is unavailable, jammed, or spoofed.",
      clusterLabel: "GNSS-Denied Navigation",
      subCapabilityId: "sensor_fusion",
      enabledByComponentCapIds: ["cc_gnss_denied_nav", "cc_ins_fusion", "cc_lidar_slam"],
      demandCount: 4,
    },
    {
      id: "sc_target_id",
      label: "Identify and track targets at range",
      description: "Detect, classify, and persistently track ground or maritime targets at operationally relevant ranges.",
      clusterLabel: "Payload-Relative Positioning",
      subCapabilityId: "eo_gimbal",
      enabledByComponentCapIds: ["cc_thermal_5km", "cc_eo_stabilization"],
      demandCount: 2,
    },
    {
      id: "sc_comms_denied",
      label: "Operate autonomously in comms-denied environment",
      description: "Continue executing the mission and return safely when the C2 link is lost or jammed.",
      clusterLabel: "Long-Range Autonomous Navigation",
      subCapabilityId: "comms_resilient",
      enabledByComponentCapIds: ["cc_px4_autopilot", "cc_redundant_comms", "cc_mesh_p2p"],
      demandCount: 3,
    },
    {
      id: "sc_terminal_guidance",
      label: "Autonomous terminal guidance to target",
      description: "Execute final-phase terminal guidance onto a designated aim point without operator intervention.",
      clusterLabel: "GNSS-Denied Navigation",
      subCapabilityId: "trn",
      enabledByComponentCapIds: ["cc_gnss_denied_nav"],
      demandCount: 1,
    },
    {
      id: "sc_swarm_coord",
      label: "Coordinate in a multi-agent swarm",
      description: "Share position and intent with peer agents and execute emergent collective behaviour without central control.",
      clusterLabel: "Swarm-Relative Navigation",
      subCapabilityId: "decentralized_swarm",
      enabledByComponentCapIds: ["cc_mesh_p2p", "cc_swarm_coord"],
      demandCount: 1,
    },
    {
      id: "sc_bvlos",
      label: "BVLOS operation in shared airspace",
      description: "Fly beyond visual line of sight while remaining compliant with DAA and communications requirements.",
      clusterLabel: "Long-Range Autonomous Navigation",
      subCapabilityId: "bvlos_nav",
      enabledByComponentCapIds: ["cc_ins_fusion", "cc_ads_b_daa", "cc_active_daa", "cc_px4_autopilot", "cc_efficient_prop"],
      demandCount: 2,
    },
    {
      id: "sc_precision_land",
      label: "Precision landing on unimproved surface",
      description: "Land with < 10 cm accuracy on unmarked or moving surfaces in wind and degraded visibility.",
      clusterLabel: "Precision Navigation & Terminal Control",
      subCapabilityId: "precision_landing",
      enabledByComponentCapIds: ["cc_precision_land"],
      demandCount: 1,
    },
    {
      id: "sc_sar_collection",
      label: "All-weather SAR imagery collection",
      description: "Collect sub-metre resolution SAR imagery through clouds, smoke, and at night.",
      clusterLabel: "Payload-Relative Positioning",
      subCapabilityId: "sar_motion",
      enabledByComponentCapIds: ["cc_sar_imaging", "cc_eo_stabilization"],
      demandCount: 1,
    },
    {
      id: "sc_cargo_delivery",
      label: "Autonomous cargo delivery",
      description: "Transport and deliver a payload to a designated location without human intervention.",
      clusterLabel: "Precision Navigation & Terminal Control",
      subCapabilityId: "winch_delivery",
      enabledByComponentCapIds: ["cc_precision_land", "cc_winch_deploy", "cc_ads_b_daa", "cc_px4_autopilot"],
      demandCount: 0,
    },
    {
      id: "sc_persistent_isr",
      label: "Persistent wide-area ISR",
      description: "Maintain continuous sensor coverage over a large area for extended dwell times.",
      clusterLabel: "Payload-Relative Positioning",
      subCapabilityId: "eo_gimbal",
      enabledByComponentCapIds: ["cc_thermal_5km", "cc_sar_imaging"],
      demandCount: 2,
    },
    {
      id: "sc_indoor_nav",
      label: "Navigate and map indoor / confined environments",
      description: "Autonomously navigate and map GPS-denied indoor spaces, tunnels, and confined structures.",
      clusterLabel: "Urban & Indoor Navigation",
      subCapabilityId: "slam",
      enabledByComponentCapIds: ["cc_lidar_slam"],
      demandCount: 0,
    },
    {
      id: "sc_perimeter_patrol",
      label: "Autonomous perimeter surveillance",
      description: "Execute repeatable autonomous patrol patterns along a defined perimeter with intrusion detection.",
      clusterLabel: "Urban & Indoor Navigation",
      subCapabilityId: "slam",
      enabledByComponentCapIds: ["cc_active_daa", "cc_redundant_comms"],
      demandCount: 1,
    },
  ],

  // ── System Types ─────────────────────────────────────────────────────────────
  systemTypes: [
    {
      id: "st_loitering_munition",
      label: "Loitering Munition",
      description: "One-way attack UAV that loiters over a target area before executing a terminal dive onto a selected aim point.",
      domains: ["defense"],
      systemCapabilityIds: ["sc_terminal_guidance", "sc_gnss_denied", "sc_comms_denied"],
      componentTypeIds: ["nav_module", "eo_ir_gimbal", "propulsion_system"],
    },
    {
      id: "st_tactical_uas",
      label: "Tactical UAS",
      description: "Man-portable or vehicle-launched unmanned aircraft for short-to-medium range ISR and strike support.",
      domains: ["defense"],
      systemCapabilityIds: ["sc_bvlos", "sc_persistent_isr", "sc_gnss_denied", "sc_comms_denied"],
      componentTypeIds: ["nav_module", "eo_ir_gimbal", "daa_system", "flight_controller"],
    },
    {
      id: "st_male_uav",
      label: "MALE UAV",
      description: "Medium-altitude long-endurance UAV for persistent multi-sensor surveillance and strike missions.",
      domains: ["defense"],
      systemCapabilityIds: ["sc_bvlos", "sc_persistent_isr", "sc_sar_collection", "sc_comms_denied", "sc_target_id"],
      componentTypeIds: ["nav_module", "eo_ir_gimbal", "sar_payload", "daa_system", "flight_controller"],
    },
    {
      id: "st_fpv_drone",
      label: "FPV Drone",
      description: "First-person view drone optimised for high-speed terminal attack or close reconnaissance.",
      domains: ["defense"],
      systemCapabilityIds: ["sc_terminal_guidance", "sc_comms_denied"],
      componentTypeIds: ["flight_controller", "propulsion_system"],
    },
    {
      id: "st_drone_in_box",
      label: "Drone-in-a-Box",
      description: "Fully automated drone deployment station enabling repeated sorties without operator presence.",
      domains: ["defense", "industrial"],
      systemCapabilityIds: ["sc_precision_land", "sc_perimeter_patrol", "sc_cargo_delivery"],
      componentTypeIds: ["nav_module", "eo_ir_gimbal", "precision_landing_sys", "flight_controller"],
    },
    {
      id: "st_swarm_uas",
      label: "Swarm UAS",
      description: "Multiple coordinated UAVs acting as a collective system with emergent group behaviour.",
      domains: ["defense"],
      systemCapabilityIds: ["sc_swarm_coord", "sc_comms_denied", "sc_gnss_denied"],
      componentTypeIds: ["nav_module", "mesh_radio", "flight_controller"],
    },
  ],

  // ── System Instances ─────────────────────────────────────────────────────────
  systemInstances: [
    {
      id: "si_ftuas_concept",
      label: "FTUAS Concept Platform",
      description: "US Army Future Tactical UAS requirement — a Group 2/3 UAS replacing RQ-7 Shadow with vertical take-off capability.",
      systemTypeIds: ["st_tactical_uas"],
      tenderId: "tender_ftuas",
      systemCapabilityIds: ["sc_bvlos", "sc_persistent_isr", "sc_gnss_denied", "sc_comms_denied"],
      componentInstanceIds: [],
    },
    {
      id: "si_switchblade_600",
      label: "Switchblade 600",
      description: "AeroVironment loitering munition with INS/GPS and EO/IR seeker for precision strike at 40+ km range.",
      systemTypeIds: ["st_loitering_munition"],
      companyId: "aerovironment",
      systemCapabilityIds: ["sc_terminal_guidance", "sc_gnss_denied", "sc_comms_denied"],
      componentInstanceIds: ["ci_vn200", "ci_boson640"],
    },
    {
      id: "si_mq9b",
      label: "MQ-9B SeaGuardian",
      description: "General Atomics MALE UAS with SATCOM, SAR, multi-spectral EO/IR, and 40+ hour endurance.",
      systemTypeIds: ["st_male_uav"],
      companyId: "general_atomics",
      systemCapabilityIds: ["sc_bvlos", "sc_persistent_isr", "sc_sar_collection", "sc_comms_denied", "sc_target_id"],
      componentInstanceIds: ["ci_sigma30", "ci_fuze", "ci_minisar"],
    },
    {
      id: "si_roadrunner",
      label: "Roadrunner-M",
      description: "Anduril vertical-take-off loitering munition with jet propulsion and autonomous terminal guidance.",
      systemTypeIds: ["st_loitering_munition"],
      companyId: "anduril",
      tenderId: "tender_roadrunner",
      systemCapabilityIds: ["sc_terminal_guidance", "sc_comms_denied", "sc_gnss_denied"],
      componentInstanceIds: ["ci_hg4930", "ci_wing_radio"],
    },
    {
      id: "si_offset_swarm",
      label: "OFFSET Swarm Agent",
      description: "DARPA OFFSET programme concept — 250-agent autonomous swarm for urban combined-arms operations.",
      systemTypeIds: ["st_swarm_uas"],
      tenderId: "tender_offset",
      systemCapabilityIds: ["sc_swarm_coord", "sc_comms_denied", "sc_gnss_denied"],
      componentInstanceIds: ["ci_l3_tac_radio"],
    },
  ],

  // ── Tenders ──────────────────────────────────────────────────────────────────
  tenders: [
    {
      id: "tender_ftuas",
      label: "US Army Future Tactical UAS (FTUAS)",
      description: "Army requirement for a Group 2/3 VTOL UAS to replace RQ-7 Shadow — requires organic SIGINT, EO/IR, and GNSS-denied capability.",
      sourceType: "formal_tender",
      specificity: 0.85,
      date: "2023-09",
      contractingAuthority: "US Army PEO Aviation",
      estimatedValue: "$400–900 M (IDIQ)",
      domains: ["defense"],
      systemInstanceIds: ["si_ftuas_concept"],
      systemTypeIds: ["st_tactical_uas"],
    },
    {
      id: "tender_offset",
      label: "DARPA OFFSET Swarm Tactics",
      description: "Programme developing swarm autonomy for 250-agent UxS teams operating in contested urban environments without GPS or continuous comms.",
      sourceType: "program_announcement",
      specificity: 0.65,
      date: "2022-06",
      contractingAuthority: "DARPA TTO",
      estimatedValue: "$60–120 M",
      domains: ["defense"],
      systemInstanceIds: ["si_offset_swarm"],
      systemTypeIds: ["st_swarm_uas"],
    },
    {
      id: "tender_nato_bvlos",
      label: "NATO BVLOS Border Surveillance",
      description: "NATO Alliance Ground Surveillance follow-on requirement for persistent BVLOS border and maritime surveillance with integrated DAA.",
      sourceType: "formal_tender",
      specificity: 0.70,
      date: "2024-02",
      contractingAuthority: "NATO C3 Agency",
      estimatedValue: "$150–300 M",
      domains: ["defense"],
      systemInstanceIds: ["si_ftuas_concept", "si_mq9b"],
      systemTypeIds: ["st_tactical_uas", "st_male_uav"],
    },
    {
      id: "tender_roadrunner",
      label: "Anduril Roadrunner-M Announcement",
      description: "Anduril press announcement of vertical take-off loitering munition with jet propulsion and reusable airframe targeting A2/AD environments.",
      sourceType: "press_release",
      specificity: 0.30,
      date: "2023-10",
      contractingAuthority: "Anduril Industries",
      domains: ["defense"],
      systemInstanceIds: ["si_roadrunner"],
      systemTypeIds: ["st_loitering_munition"],
    },
  ],

  // ── Component Instances ───────────────────────────────────────────────────────
  componentInstances: [
    {
      id: "ci_vn200",
      label: "VectorNav VN-200",
      description: "High-reliability tactical-grade GNSS/INS with dual-antenna heading for GNSS-denied flight.",
      companyId: "vectornav",
      componentTypeId: "nav_module",
      componentCapabilityIds: ["cc_gnss_denied_nav", "cc_ins_fusion"],
    },
    {
      id: "ci_sigma30",
      label: "Safran Sigma 30 INS",
      description: "Navigation-grade ring laser gyro INS with integrated barometric altitude — MIL-SPEC qualified.",
      companyId: "safran",
      componentTypeId: "nav_module",
      componentCapabilityIds: ["cc_gnss_denied_nav", "cc_ins_fusion"],
    },
    {
      id: "ci_hg4930",
      label: "Honeywell HG4930 IMU",
      description: "MEMS-based tactical IMU with < 1° CEP positioning after 60 s GPS outage.",
      companyId: "honeywell_defense",
      componentTypeId: "nav_module",
      componentCapabilityIds: ["cc_ins_fusion"],
    },
    {
      id: "ci_certus",
      label: "Advanced Navigation Certus",
      description: "Miniaturised INS/GNSS with integrated magnetometer and 200 Hz output for tactical UAS.",
      companyId: "advanced_navigation",
      componentTypeId: "nav_module",
      componentCapabilityIds: ["cc_ins_fusion", "cc_gnss_denied_nav"],
    },
    {
      id: "ci_boson640",
      label: "Teledyne FLIR Boson 640",
      description: "640×512 LWIR thermal core weighing < 9 g — OEM-ready for loitering munition seekers.",
      companyId: "teledyne_flir",
      componentTypeId: "eo_ir_gimbal",
      componentCapabilityIds: ["cc_thermal_5km", "cc_eo_stabilization"],
    },
    {
      id: "ci_mx20",
      label: "Wescam MX-20",
      description: "Multi-spectral airborne surveillance turret with HD EO, MWIR FLIR, and laser designator.",
      companyId: "wescam",
      componentTypeId: "eo_ir_gimbal",
      componentCapabilityIds: ["cc_thermal_5km", "cc_eo_stabilization"],
    },
    {
      id: "ci_minisar",
      label: "Teledyne FLIR MiniSAR",
      description: "Compact Ku-band SAR payload producing < 0.3 m resolution strip-map imagery from Group 3+ UAS.",
      companyId: "teledyne_flir",
      componentTypeId: "sar_payload",
      componentCapabilityIds: ["cc_sar_imaging"],
    },
    {
      id: "ci_fuze",
      label: "uAvionix FUZE",
      description: "Integrated ADS-B In/Out transponder for BVLOS — DO-365 compliant in < 30 g.",
      companyId: "uavionix",
      componentTypeId: "daa_system",
      componentCapabilityIds: ["cc_ads_b_daa"],
    },
    {
      id: "ci_ping200x",
      label: "uAvionix ping200X",
      description: "Mode S/ADS-B transponder with TCAS resolution advisory output for larger UAS platforms.",
      companyId: "uavionix",
      componentTypeId: "daa_system",
      componentCapabilityIds: ["cc_ads_b_daa"],
    },
    {
      id: "ci_cygnus",
      label: "Iris Automation Cygnus",
      description: "AI-vision DAA sensor detecting non-cooperative aircraft at > 500 m — certified for BVLOS waivers.",
      companyId: "iris_automation",
      componentTypeId: "daa_system",
      componentCapabilityIds: ["cc_active_daa", "cc_ads_b_daa"],
    },
    {
      id: "ci_wing_radio",
      label: "Anduril Pulsar Mesh Radio",
      description: "MIMO mesh radio supporting 250-node peer-to-peer networks with < 30 ms end-to-end latency.",
      companyId: "anduril",
      componentTypeId: "mesh_radio",
      componentCapabilityIds: ["cc_mesh_p2p", "cc_swarm_coord"],
    },
    {
      id: "ci_l3_tac_radio",
      label: "L3Harris AN/PRC-167 Tactical Radio",
      description: "Software-defined waveform radio with MIMO mesh networking and electronic counter-countermeasures.",
      companyId: "l3harris",
      componentTypeId: "mesh_radio",
      componentCapabilityIds: ["cc_mesh_p2p", "cc_swarm_coord"],
    },
    {
      id: "ci_shield_swarm",
      label: "Shield AI Hivemind Flight Stack",
      description: "AI-native autonomy software stack enabling GPS/comms-denied swarm flight on commodity hardware.",
      companyId: "shield_ai",
      componentTypeId: "flight_controller",
      componentCapabilityIds: ["cc_px4_autopilot", "cc_redundant_comms"],
    },
    {
      id: "ci_zipline_pds",
      label: "Zipline Precision Delivery System",
      description: "Patented hover-winch payload delivery mechanism achieving ± 0.3 m placement accuracy at speed.",
      companyId: "zipline",
      componentTypeId: "precision_landing_sys",
      componentCapabilityIds: ["cc_precision_land", "cc_winch_deploy"],
    },
    {
      id: "ci_skydio_x10_nav",
      label: "Skydio X10 Navigation Stack",
      description: "Obstacle-aware visual-inertial navigation stack with 6-camera 360° depth for confined environments.",
      companyId: "skydio",
      componentTypeId: "precision_landing_sys",
      componentCapabilityIds: ["cc_precision_land", "cc_lidar_slam"],
    },
  ],

  // ── Companies ────────────────────────────────────────────────────────────────
  companies: [
    {
      id: "aerovironment",
      label: "AeroVironment",
      description: "Developer of tactical unmanned aircraft systems and loitering munitions, including Switchblade 300/600 and Puma UAS families.",
      capabilitiesCovered: ["vio", "trn", "sensor_fusion", "precision_landing"],
      componentInstanceIds: [],
      systemInstanceIds: ["si_switchblade_600"],
      companyTypes: ["oem"],
      domains: ["defense"],
      icp: [{ id: "defense_oem", label: "Defense Drone OEMs", description: "Prime contractors building military UAS platforms" }],
    },
    // GNSS-denied navigation companies
    {
      id: "skydio",
      label: "Skydio",
      description: "Autonomous drone platform specializing in obstacle avoidance and AI-powered navigation for defense and enterprise.",
      capabilitiesCovered: ["vio", "trn", "sensor_fusion", "precision_landing", "slam", "human_safe_corridors"],
      componentInstanceIds: ["ci_skydio_x10_nav"],
      systemInstanceIds: [],
      companyTypes: ["oem"],
      domains: ["defense", "industrial"],
      icp: [
        { id: "defense_oem", label: "Defense Drone OEMs", description: "Prime contractors building military UAS platforms" },
        { id: "public_safety", label: "Public Safety Agencies", description: "Law enforcement and emergency response" },
      ],
    },
    {
      id: "shield_ai",
      label: "Shield AI",
      description: "AI-powered autonomy stack for drones and aircraft operating in GPS-denied, communications-denied environments.",
      capabilitiesCovered: ["vio", "rf_positioning", "map_matching", "rel_positioning", "decentralized_swarm"],
      componentInstanceIds: ["ci_shield_swarm"],
      systemInstanceIds: [],
      companyTypes: ["software", "integrator"],
      domains: ["defense"],
      icp: [
        { id: "defense_oem", label: "Defense Drone OEMs", description: "Prime contractors building military UAS platforms" },
        { id: "swarm_integrators", label: "Swarm System Integrators", description: "Companies integrating multi-agent drone systems" },
      ],
    },
    {
      id: "advanced_navigation",
      label: "Advanced Navigation",
      description: "Inertial navigation systems and sensor fusion solutions for high-precision positioning in challenging environments.",
      capabilitiesCovered: ["sensor_fusion", "trn", "acoustic_nav"],
      componentInstanceIds: ["ci_certus"],
      systemInstanceIds: [],
      companyTypes: ["supplier"],
      domains: ["defense", "industrial"],
      icp: [
        { id: "defense_oem", label: "Defense Drone OEMs", description: "Prime contractors building military UAS platforms" },
        { id: "maritime_operators", label: "Maritime Operators", description: "Naval and commercial maritime organizations" },
      ],
    },
    {
      id: "safran",
      label: "Safran",
      description: "Defense electronics and navigation systems manufacturer providing inertial measurement units and navigation solutions.",
      capabilitiesCovered: ["sensor_fusion", "trn", "eo_gimbal"],
      componentInstanceIds: ["ci_sigma30"],
      systemInstanceIds: [],
      companyTypes: ["oem", "supplier"],
      domains: ["defense"],
      icp: [{ id: "defense_oem", label: "Defense Drone OEMs", description: "Prime contractors building military UAS platforms" }],
    },
    {
      id: "teledyne_flir",
      label: "Teledyne FLIR",
      description: "Thermal imaging and sensing solutions for ISR, maritime surveillance, and precision targeting.",
      capabilitiesCovered: ["eo_gimbal", "sar_motion", "lidar_coupled"],
      componentInstanceIds: ["ci_boson640", "ci_minisar"],
      systemInstanceIds: [],
      companyTypes: ["oem", "supplier"],
      domains: ["defense", "industrial"],
      icp: [
        { id: "defense_imagery", label: "Defense Imagery Analysts", description: "Intelligence and surveillance analysis organizations" },
        { id: "border_agencies", label: "Border Security Agencies", description: "Government border control and customs enforcement" },
      ],
    },
    {
      id: "aceinna",
      label: "ACEINNA",
      description: "Open-source inertial measurement platform for autonomous vehicle and drone positioning.",
      capabilitiesCovered: ["sensor_fusion", "vio"],
      componentInstanceIds: [],
      systemInstanceIds: [],
      companyTypes: ["supplier"],
      domains: ["industrial", "defense"],
      icp: [{ id: "industrial_inspection", label: "Industrial Inspection Providers", description: "Companies providing drone-based industrial asset inspection" }],
    },
    {
      id: "inertialsense",
      label: "InertialSense",
      description: "Miniaturized GNSS-INS sensors for precision navigation in size, weight, and power constrained platforms.",
      capabilitiesCovered: ["sensor_fusion"],
      componentInstanceIds: [],
      systemInstanceIds: [],
      companyTypes: ["supplier"],
      domains: ["defense", "industrial"],
      icp: [{ id: "defense_oem", label: "Defense Drone OEMs", description: "Prime contractors building military UAS platforms" }],
    },
    {
      id: "sbg_systems",
      label: "SBG Systems",
      description: "MEMS inertial sensors and GPS/INS systems for drones, marine vessels, and autonomous platforms.",
      capabilitiesCovered: ["sensor_fusion", "trn"],
      componentInstanceIds: [],
      systemInstanceIds: [],
      companyTypes: ["supplier"],
      domains: ["defense", "industrial"],
      icp: [{ id: "surveying_firms", label: "Surveying & Mapping Firms", description: "Commercial surveying and geospatial data companies" }],
    },
    {
      id: "vectornav",
      label: "VectorNav",
      description: "High-reliability GNSS/INS solutions for unmanned systems operating in denied environments.",
      capabilitiesCovered: ["sensor_fusion"],
      componentInstanceIds: ["ci_vn200"],
      systemInstanceIds: [],
      companyTypes: ["supplier"],
      domains: ["defense"],
      icp: [{ id: "defense_oem", label: "Defense Drone OEMs", description: "Prime contractors building military UAS platforms" }],
    },
    {
      id: "honeywell_defense",
      label: "Honeywell Defense",
      description: "Aerospace navigation systems including ring laser gyroscopes and integrated avionics.",
      capabilitiesCovered: ["sensor_fusion", "trn", "bvlos_nav"],
      componentInstanceIds: ["ci_hg4930"],
      systemInstanceIds: [],
      companyTypes: ["oem", "supplier"],
      domains: ["defense"],
      icp: [{ id: "defense_oem", label: "Defense Drone OEMs", description: "Prime contractors building military UAS platforms" }],
    },
    // Precision nav companies
    {
      id: "dji",
      label: "DJI",
      description: "World's largest commercial drone manufacturer with comprehensive autonomous flight and landing capabilities.",
      capabilitiesCovered: ["precision_landing", "geo_fenced", "rth_failsafe", "winch_delivery"],
      componentInstanceIds: [],
      systemInstanceIds: [],
      companyTypes: ["oem"],
      domains: ["industrial"],
      icp: [
        { id: "logistics_operators", label: "Logistics Operators", description: "Commercial last-mile delivery and logistics companies" },
        { id: "agri_analytics", label: "Agricultural Analytics Providers", description: "Precision agriculture data and analytics firms" },
      ],
    },
    {
      id: "percepto",
      label: "Percepto",
      description: "Autonomous drone-in-a-box platform for continuous industrial site monitoring and inspection.",
      capabilitiesCovered: ["precision_landing", "geo_fenced", "slam", "depth_camera"],
      componentInstanceIds: [],
      systemInstanceIds: [],
      companyTypes: ["oem", "software"],
      domains: ["industrial"],
      icp: [
        { id: "industrial_inspection", label: "Industrial Inspection Providers", description: "Companies providing drone-based industrial asset inspection" },
        { id: "warehouse_operators", label: "Warehouse Operators", description: "Large-scale warehousing and distribution center operators" },
      ],
    },
    {
      id: "airobotics",
      label: "Airobotics",
      description: "Fully automatic drone platform for industrial and government outdoor security applications.",
      capabilitiesCovered: ["precision_landing", "geo_fenced", "rth_failsafe"],
      componentInstanceIds: [],
      systemInstanceIds: [],
      companyTypes: ["oem"],
      domains: ["industrial", "defense"],
      icp: [{ id: "industrial_inspection", label: "Industrial Inspection Providers", description: "Companies providing drone-based industrial asset inspection" }],
    },
    {
      id: "matternet",
      label: "Matternet",
      description: "Urban drone delivery network platform specializing in precision cargo transport and landing.",
      capabilitiesCovered: ["precision_landing", "winch_delivery"],
      componentInstanceIds: [],
      systemInstanceIds: [],
      companyTypes: ["oem", "software"],
      domains: ["industrial"],
      icp: [
        { id: "medical_ngos", label: "Medical Delivery NGOs", description: "Healthcare and humanitarian organizations using drone delivery" },
        { id: "logistics_operators", label: "Logistics Operators", description: "Commercial last-mile delivery and logistics companies" },
      ],
    },
    {
      id: "zipline",
      label: "Zipline",
      description: "Fixed-wing autonomous drone delivery platform for medical supplies and commercial goods at scale.",
      capabilitiesCovered: ["winch_delivery", "rth_failsafe", "bvlos_nav", "energy_route"],
      componentInstanceIds: ["ci_zipline_pds"],
      systemInstanceIds: [],
      companyTypes: ["oem", "software"],
      domains: ["industrial"],
      icp: [
        { id: "medical_ngos", label: "Medical Delivery NGOs", description: "Healthcare and humanitarian organizations using drone delivery" },
        { id: "logistics_operators", label: "Logistics Operators", description: "Commercial last-mile delivery and logistics companies" },
      ],
    },
    {
      id: "iris_automation",
      label: "Iris Automation",
      description: "AI-based detect-and-avoid system enabling BVLOS drone operations in uncontrolled airspace.",
      capabilitiesCovered: ["daa", "bvlos_nav", "geo_fenced"],
      componentInstanceIds: ["ci_cygnus"],
      systemInstanceIds: [],
      companyTypes: ["software", "supplier"],
      domains: ["industrial", "defense"],
      icp: [
        { id: "cargo_logistics", label: "Cargo Logistics Providers", description: "Freight and cargo delivery companies using UAS" },
        { id: "energy_operators", label: "Energy Infrastructure Operators", description: "Utilities and pipeline operators using drone inspection" },
      ],
    },
    {
      id: "joby_aviation",
      label: "Joby Aviation",
      description: "Electric vertical takeoff and landing (eVTOL) aircraft developer with precision navigation capabilities.",
      capabilitiesCovered: ["precision_landing", "autonomous_docking", "leader_follower"],
      componentInstanceIds: [],
      systemInstanceIds: [],
      companyTypes: ["oem"],
      domains: ["industrial"],
      icp: [{ id: "urban_air_mobility", label: "Urban Air Mobility Integrators", description: "Companies building urban air taxi and cargo networks" }],
    },
    {
      id: "beta_technologies",
      label: "Beta Technologies",
      description: "Electric aviation company developing autonomous charging infrastructure and precision landing for eVTOL.",
      capabilitiesCovered: ["autonomous_docking", "precision_landing"],
      componentInstanceIds: [],
      systemInstanceIds: [],
      companyTypes: ["oem"],
      domains: ["industrial"],
      icp: [{ id: "urban_air_mobility", label: "Urban Air Mobility Integrators", description: "Companies building urban air taxi and cargo networks" }],
    },
    // Swarm companies
    {
      id: "anduril",
      label: "Anduril Industries",
      description: "Defense technology company building autonomous systems and AI-powered command and control for military applications.",
      capabilitiesCovered: ["rel_positioning", "decentralized_swarm", "mesh_comms", "swarm_collision"],
      componentInstanceIds: ["ci_wing_radio"],
      systemInstanceIds: ["si_roadrunner"],
      companyTypes: ["oem", "software"],
      domains: ["defense"],
      icp: [
        { id: "defense_oem", label: "Defense Drone OEMs", description: "Prime contractors building military UAS platforms" },
        { id: "swarm_integrators", label: "Swarm System Integrators", description: "Companies integrating multi-agent drone systems" },
      ],
    },
    {
      id: "uvision",
      label: "UVision",
      description: "Loitering munition systems with precision terminal guidance and cooperative strike capabilities.",
      capabilitiesCovered: ["rel_positioning", "decentralized_swarm"],
      componentInstanceIds: [],
      systemInstanceIds: [],
      companyTypes: ["oem"],
      domains: ["defense"],
      icp: [{ id: "swarm_integrators", label: "Swarm System Integrators", description: "Companies integrating multi-agent drone systems" }],
    },
    {
      id: "heron_systems",
      label: "Heron Systems",
      description: "AI algorithms for autonomous air combat and swarm coordination, acquired by Shield AI.",
      capabilitiesCovered: ["decentralized_swarm", "leader_follower", "mesh_comms"],
      componentInstanceIds: [],
      systemInstanceIds: [],
      companyTypes: ["software"],
      domains: ["defense"],
      icp: [{ id: "swarm_integrators", label: "Swarm System Integrators", description: "Companies integrating multi-agent drone systems" }],
    },
    {
      id: "swarming_logic",
      label: "Swarming Logic",
      description: "Autonomous swarm coordination software for multi-drone ISR and search and rescue operations.",
      capabilitiesCovered: ["decentralized_swarm", "swarm_collision", "rel_positioning"],
      componentInstanceIds: [],
      systemInstanceIds: [],
      companyTypes: ["software"],
      domains: ["defense", "industrial"],
      icp: [
        { id: "swarm_integrators", label: "Swarm System Integrators", description: "Companies integrating multi-agent drone systems" },
        { id: "emergency_response", label: "Emergency Response Agencies", description: "Fire, rescue, and disaster response organizations" },
      ],
    },
    {
      id: "l3harris",
      label: "L3Harris Technologies",
      description: "Defense electronics prime contractor providing communications, sensing, and autonomous systems.",
      capabilitiesCovered: ["mesh_comms", "swarm_collision", "bvlos_nav", "daa"],
      componentInstanceIds: ["ci_l3_tac_radio"],
      systemInstanceIds: [],
      companyTypes: ["oem", "integrator"],
      domains: ["defense"],
      icp: [
        { id: "defense_oem", label: "Defense Drone OEMs", description: "Prime contractors building military UAS platforms" },
        { id: "border_agencies", label: "Border Security Agencies", description: "Government border control and customs enforcement" },
      ],
    },
    // Payload-relative positioning companies
    {
      id: "phase_one",
      label: "Phase One",
      description: "High-resolution aerial imaging systems and cameras for precision mapping and intelligence gathering.",
      capabilitiesCovered: ["eo_gimbal", "hyperspectral"],
      componentInstanceIds: [],
      systemInstanceIds: [],
      companyTypes: ["supplier", "oem"],
      domains: ["defense", "industrial"],
      icp: [
        { id: "defense_imagery", label: "Defense Imagery Analysts", description: "Intelligence and surveillance analysis organizations" },
        { id: "surveying_firms", label: "Surveying & Mapping Firms", description: "Commercial surveying and geospatial data companies" },
      ],
    },
    {
      id: "micasense",
      label: "MicaSense",
      description: "Multispectral and hyperspectral cameras for agricultural mapping and crop health monitoring.",
      capabilitiesCovered: ["hyperspectral", "eo_gimbal"],
      componentInstanceIds: [],
      systemInstanceIds: [],
      companyTypes: ["supplier"],
      domains: ["industrial"],
      icp: [{ id: "agri_analytics", label: "Agricultural Analytics Providers", description: "Precision agriculture data and analytics firms" }],
    },
    {
      id: "velodyne",
      label: "Velodyne Lidar",
      description: "360-degree LiDAR sensors for autonomous navigation, mapping, and obstacle detection.",
      capabilitiesCovered: ["lidar_coupled", "sar_motion"],
      componentInstanceIds: [],
      systemInstanceIds: [],
      companyTypes: ["supplier"],
      domains: ["industrial", "defense"],
      icp: [
        { id: "surveying_firms", label: "Surveying & Mapping Firms", description: "Commercial surveying and geospatial data companies" },
        { id: "infrastructure_managers", label: "Infrastructure Asset Managers", description: "Companies managing bridges, power lines, and critical infrastructure" },
      ],
    },
    {
      id: "ouster",
      label: "Ouster",
      description: "Digital LiDAR sensors for robotics, autonomous vehicles, and industrial automation.",
      capabilitiesCovered: ["lidar_coupled"],
      componentInstanceIds: [],
      systemInstanceIds: [],
      companyTypes: ["supplier"],
      domains: ["industrial"],
      icp: [{ id: "infrastructure_managers", label: "Infrastructure Asset Managers", description: "Companies managing bridges, power lines, and critical infrastructure" }],
    },
    {
      id: "trimble",
      label: "Trimble",
      description: "Positioning and geospatial solutions for surveying, construction, and precision agriculture.",
      capabilitiesCovered: ["lidar_coupled", "hyperspectral"],
      componentInstanceIds: [],
      systemInstanceIds: [],
      companyTypes: ["oem", "software"],
      domains: ["industrial"],
      icp: [
        { id: "surveying_firms", label: "Surveying & Mapping Firms", description: "Commercial surveying and geospatial data companies" },
        { id: "agri_analytics", label: "Agricultural Analytics Providers", description: "Precision agriculture data and analytics firms" },
      ],
    },
    {
      id: "leica_geosystems",
      label: "Leica Geosystems",
      description: "Precision measurement and geospatial solutions for survey-grade aerial mapping.",
      capabilitiesCovered: ["lidar_coupled", "sar_motion"],
      componentInstanceIds: [],
      systemInstanceIds: [],
      companyTypes: ["oem", "supplier"],
      domains: ["industrial"],
      icp: [{ id: "surveying_firms", label: "Surveying & Mapping Firms", description: "Commercial surveying and geospatial data companies" }],
    },
    {
      id: "riegl",
      label: "RIEGL",
      description: "Waveform-processing LiDAR systems for airborne, mobile, and terrestrial scanning applications.",
      capabilitiesCovered: ["lidar_coupled"],
      componentInstanceIds: [],
      systemInstanceIds: [],
      companyTypes: ["supplier", "oem"],
      domains: ["industrial"],
      icp: [{ id: "surveying_firms", label: "Surveying & Mapping Firms", description: "Commercial surveying and geospatial data companies" }],
    },
    {
      id: "shotover",
      label: "Shotover",
      description: "Gyro-stabilized aerial camera systems for broadcast, surveillance, and ISR missions.",
      capabilitiesCovered: ["eo_gimbal"],
      componentInstanceIds: [],
      systemInstanceIds: [],
      companyTypes: ["oem", "supplier"],
      domains: ["defense", "industrial"],
      icp: [{ id: "defense_imagery", label: "Defense Imagery Analysts", description: "Intelligence and surveillance analysis organizations" }],
    },
    {
      id: "wescam",
      label: "Wescam (L3Harris)",
      description: "Multi-spectral airborne surveillance and targeting systems for military and law enforcement.",
      capabilitiesCovered: ["eo_gimbal", "sar_motion"],
      componentInstanceIds: ["ci_mx20"],
      systemInstanceIds: [],
      companyTypes: ["oem"],
      domains: ["defense"],
      icp: [
        { id: "defense_imagery", label: "Defense Imagery Analysts", description: "Intelligence and surveillance analysis organizations" },
        { id: "border_agencies", label: "Border Security Agencies", description: "Government border control and customs enforcement" },
      ],
    },
    // Urban & indoor navigation companies
    {
      id: "flyability",
      label: "Flyability",
      description: "Collision-tolerant indoor drones for inspection of confined, GPS-denied spaces.",
      capabilitiesCovered: ["slam", "depth_camera", "acoustic_nav", "floor_plan"],
      componentInstanceIds: [],
      systemInstanceIds: [],
      companyTypes: ["oem"],
      domains: ["industrial"],
      icp: [
        { id: "oil_gas", label: "Oil & Gas Facility Operators", description: "Refineries, platforms, and pipeline operators requiring confined space inspection" },
        { id: "industrial_inspection", label: "Industrial Inspection Providers", description: "Companies providing drone-based industrial asset inspection" },
      ],
    },
    {
      id: "gecko_robotics",
      label: "Gecko Robotics",
      description: "Robotic inspection systems for critical industrial infrastructure including tanks and pressure vessels.",
      capabilitiesCovered: ["slam", "floor_plan"],
      componentInstanceIds: [],
      systemInstanceIds: [],
      companyTypes: ["oem", "software"],
      domains: ["industrial"],
      icp: [{ id: "oil_gas", label: "Oil & Gas Facility Operators", description: "Refineries, platforms, and pipeline operators requiring confined space inspection" }],
    },
    {
      id: "impossible_aerospace",
      label: "Impossible Aerospace",
      description: "Long-endurance electric drones designed for public safety and industrial inspection operations.",
      capabilitiesCovered: ["slam", "human_safe_corridors"],
      componentInstanceIds: [],
      systemInstanceIds: [],
      companyTypes: ["oem"],
      domains: ["industrial", "defense"],
      icp: [{ id: "public_safety", label: "Public Safety Agencies", description: "Law enforcement and emergency response" }],
    },
    {
      id: "azur_drones",
      label: "Azur Drones",
      description: "Automated drone-in-a-box solutions for industrial security and perimeter monitoring.",
      capabilitiesCovered: ["slam", "depth_camera"],
      componentInstanceIds: [],
      systemInstanceIds: [],
      companyTypes: ["oem"],
      domains: ["industrial"],
      icp: [
        { id: "industrial_inspection", label: "Industrial Inspection Providers", description: "Companies providing drone-based industrial asset inspection" },
        { id: "telecom_managers", label: "Telecom Tower Managers", description: "Telecommunications infrastructure operators requiring periodic inspection" },
      ],
    },
    {
      id: "brinc_drones",
      label: "BRINC Drones",
      description: "Indoor tactical drones for law enforcement, hostage rescue, and SWAT operations.",
      capabilitiesCovered: ["slam", "depth_camera", "human_safe_corridors"],
      componentInstanceIds: [],
      systemInstanceIds: [],
      companyTypes: ["oem"],
      domains: ["defense"],
      icp: [{ id: "public_safety", label: "Public Safety Agencies", description: "Law enforcement and emergency response" }],
    },
    {
      id: "ceva_inc",
      label: "CEVA Inc.",
      description: "IP licensing for smart edge processors and sensor fusion for IoT and drone navigation.",
      capabilitiesCovered: ["depth_camera", "sensor_fusion"],
      componentInstanceIds: [],
      systemInstanceIds: [],
      companyTypes: ["supplier"],
      domains: ["industrial"],
      icp: [{ id: "warehouse_operators", label: "Warehouse Operators", description: "Large-scale warehousing and distribution center operators" }],
    },
    // Long-range autonomous nav companies
    {
      id: "general_atomics",
      label: "General Atomics",
      description: "Prime defense contractor producing Predator/Reaper UAS with long-range autonomous navigation.",
      capabilitiesCovered: ["bvlos_nav", "daa", "terrain_following", "comms_resilient"],
      componentInstanceIds: [],
      systemInstanceIds: ["si_mq9b"],
      companyTypes: ["oem"],
      domains: ["defense"],
      icp: [
        { id: "border_agencies", label: "Border Security Agencies", description: "Government border control and customs enforcement" },
        { id: "defense_oem", label: "Defense Drone OEMs", description: "Prime contractors building military UAS platforms" },
      ],
    },
    {
      id: "northrop_grumman",
      label: "Northrop Grumman",
      description: "Autonomous systems prime contractor producing Global Hawk and Triton HALE UAS.",
      capabilitiesCovered: ["bvlos_nav", "daa", "comms_resilient", "energy_route"],
      componentInstanceIds: [],
      systemInstanceIds: [],
      companyTypes: ["oem", "integrator"],
      domains: ["defense"],
      icp: [
        { id: "defense_oem", label: "Defense Drone OEMs", description: "Prime contractors building military UAS platforms" },
        { id: "maritime_operators", label: "Maritime Operators", description: "Naval and commercial maritime organizations" },
      ],
    },
    {
      id: "uavionix",
      label: "uAvionix",
      description: "Miniaturized ADS-B transponders and detect-and-avoid systems for BVLOS drone operations.",
      capabilitiesCovered: ["daa", "bvlos_nav"],
      componentInstanceIds: ["ci_fuze", "ci_ping200x"],
      systemInstanceIds: [],
      companyTypes: ["supplier"],
      domains: ["industrial", "defense"],
      icp: [
        { id: "cargo_logistics", label: "Cargo Logistics Providers", description: "Freight and cargo delivery companies using UAS" },
        { id: "energy_operators", label: "Energy Infrastructure Operators", description: "Utilities and pipeline operators using drone inspection" },
      ],
    },
    {
      id: "senseFly",
      label: "senseFly (Parrot)",
      description: "Professional fixed-wing mapping drones for surveying, agriculture, and infrastructure monitoring.",
      capabilitiesCovered: ["bvlos_nav", "terrain_following", "energy_route"],
      componentInstanceIds: [],
      systemInstanceIds: [],
      companyTypes: ["oem"],
      domains: ["industrial"],
      icp: [
        { id: "surveying_firms", label: "Surveying & Mapping Firms", description: "Commercial surveying and geospatial data companies" },
        { id: "energy_operators", label: "Energy Infrastructure Operators", description: "Utilities and pipeline operators using drone inspection" },
      ],
    },
    {
      id: "wingcopter",
      label: "Wingcopter",
      description: "Tiltrotor drone delivery platform for last-mile logistics and medical supply in remote areas.",
      capabilitiesCovered: ["bvlos_nav", "winch_delivery", "energy_route"],
      componentInstanceIds: [],
      systemInstanceIds: [],
      companyTypes: ["oem"],
      domains: ["industrial"],
      icp: [
        { id: "medical_ngos", label: "Medical Delivery NGOs", description: "Healthcare and humanitarian organizations using drone delivery" },
        { id: "cargo_logistics", label: "Cargo Logistics Providers", description: "Freight and cargo delivery companies using UAS" },
      ],
    },
    {
      id: "wing",
      label: "Wing (Alphabet)",
      description: "Commercial drone delivery service operating BVLOS routes for retail and medical deliveries.",
      capabilitiesCovered: ["bvlos_nav", "daa", "geo_fenced"],
      componentInstanceIds: [],
      systemInstanceIds: [],
      companyTypes: ["oem", "software"],
      domains: ["industrial"],
      icp: [{ id: "logistics_operators", label: "Logistics Operators", description: "Commercial last-mile delivery and logistics companies" }],
    },
    {
      id: "sabrewing",
      label: "Sabrewing",
      description: "Large autonomous cargo drone platform for long-range heavy payload delivery.",
      capabilitiesCovered: ["bvlos_nav", "terrain_following", "comms_resilient"],
      componentInstanceIds: [],
      systemInstanceIds: [],
      companyTypes: ["oem"],
      domains: ["industrial", "defense"],
      icp: [
        { id: "cargo_logistics", label: "Cargo Logistics Providers", description: "Freight and cargo delivery companies using UAS" },
        { id: "defense_oem", label: "Defense Drone OEMs", description: "Prime contractors building military UAS platforms" },
      ],
    },
    {
      id: "garmin_aviation",
      label: "Garmin Aviation",
      description: "Avionics and GPS navigation systems for general aviation and unmanned aircraft systems.",
      capabilitiesCovered: ["daa", "bvlos_nav", "terrain_following"],
      componentInstanceIds: [],
      systemInstanceIds: [],
      companyTypes: ["oem", "supplier"],
      domains: ["industrial"],
      icp: [{ id: "cargo_logistics", label: "Cargo Logistics Providers", description: "Freight and cargo delivery companies using UAS" }],
    },
    {
      id: "overwatch_imaging",
      label: "Overwatch Imaging",
      description: "Wide-area persistent surveillance camera systems for ISR and border security applications.",
      capabilitiesCovered: ["eo_gimbal", "hyperspectral"],
      componentInstanceIds: [],
      systemInstanceIds: [],
      companyTypes: ["oem", "supplier"],
      domains: ["defense"],
      icp: [
        { id: "border_agencies", label: "Border Security Agencies", description: "Government border control and customs enforcement" },
        { id: "defense_imagery", label: "Defense Imagery Analysts", description: "Intelligence and surveillance analysis organizations" },
      ],
    },
  ],

  // ── Capability Clusters (unchanged for sunburst / heatmap) ──────────────────
  clusters: [
    {
      id: "gnss_denied",
      label: "GNSS-Denied Navigation",
      description: "Navigation capabilities that maintain accurate positioning and autonomy when GPS signals are unavailable, jammed, or spoofed.",
      subCapabilities: [
        {
          id: "vio",
          label: "Visual-Inertial Odometry (VIO)",
          description: "Fuses camera and IMU data to estimate position and orientation without external signals.",
          whyItMatters: "Foundation of indoor and urban autonomous flight — enables drones to operate in environments where GPS is unavailable.",
          typicalBuyers: ["Defense drone OEMs", "Search & rescue operators", "Industrial inspection providers"],
          companyIds: ["skydio", "shield_ai", "aceinna"],
          systemCapabilityIds: ["sc_gnss_denied"],
          useCases: [
            {
              id: "isr_contested",
              label: "ISR in Contested Environments",
              description: "Intelligence, surveillance, and reconnaissance missions where adversary jamming degrades GPS.",
              marketStatus: "crowded",
              useCaseTags: ["isr", "surveillance"],
              domains: ["defense"],
              companyIds: ["skydio", "shield_ai"],
            },
            {
              id: "indoor_inspection",
              label: "Indoor Inspection Drones",
              description: "Autonomous navigation inside facilities, tanks, and structures without GPS.",
              marketStatus: "moderate",
              useCaseTags: ["inspection"],
              domains: ["industrial"],
              companyIds: ["skydio", "aceinna"],
            },
          ],
        },
        {
          id: "trn",
          label: "Terrain-Relative Navigation (TRN)",
          description: "Matches real-time sensor data against pre-loaded terrain maps to determine precise position.",
          whyItMatters: "Critical for precision strike and loitering munitions that must maintain accuracy over long flight paths.",
          typicalBuyers: ["Defense prime contractors", "Loitering munition manufacturers"],
          companyIds: ["advanced_navigation", "safran", "sbg_systems", "honeywell_defense"],
          systemCapabilityIds: ["sc_terminal_guidance", "sc_gnss_denied"],
          useCases: [
            {
              id: "loitering_munitions",
              label: "Loitering Munitions",
              description: "Precision strike weapons that navigate without GPS to designated target areas.",
              marketStatus: "crowded",
              useCaseTags: ["isr"],
              domains: ["defense"],
              companyIds: ["safran", "honeywell_defense"],
            },
            {
              id: "tunnel_mapping",
              label: "Tunnel Mapping Drones",
              description: "Mapping underground infrastructure where GPS signals cannot penetrate.",
              marketStatus: "open",
              useCaseTags: ["mapping", "inspection"],
              domains: ["industrial"],
              companyIds: ["advanced_navigation", "sbg_systems"],
            },
          ],
        },
        {
          id: "rf_positioning",
          label: "RF-Based Positioning",
          description: "Uses radio frequency signals (UWB, LoRa, cellular) as positioning anchors in GPS-denied areas.",
          whyItMatters: "Enables reliable indoor positioning without cameras or complex sensors — scalable for warehouse and facility applications.",
          typicalBuyers: ["Warehouse operators", "Industrial facility managers"],
          companyIds: ["shield_ai"],
          useCases: [
            {
              id: "gps_jammed_surveillance",
              label: "GPS-Jammed Border Surveillance",
              description: "Persistent monitoring of border zones subject to deliberate jamming.",
              marketStatus: "open",
              useCaseTags: ["surveillance"],
              domains: ["defense"],
              companyIds: ["shield_ai"],
            },
          ],
        },
        {
          id: "sensor_fusion",
          label: "Sensor Fusion Navigation",
          description: "Integrates data from multiple sensors (IMU, barometer, magnetometer, vision, LiDAR) for robust positioning.",
          whyItMatters: "Redundancy and accuracy — no single sensor failure can cause total navigation loss.",
          typicalBuyers: ["Defense OEMs", "Commercial drone platform manufacturers"],
          companyIds: ["advanced_navigation", "safran", "aceinna", "inertialsense", "sbg_systems", "vectornav", "honeywell_defense", "ceva_inc"],
          systemCapabilityIds: ["sc_gnss_denied"],
          useCases: [
            {
              id: "isr_missions",
              label: "Persistent ISR Missions",
              description: "Long-duration surveillance requiring continuous accurate positioning despite signal degradation.",
              marketStatus: "crowded",
              useCaseTags: ["isr", "surveillance"],
              domains: ["defense"],
              companyIds: ["advanced_navigation", "safran", "vectornav", "honeywell_defense"],
            },
          ],
        },
        {
          id: "map_matching",
          label: "Map-Matching Navigation",
          description: "Matches observed sensor patterns against pre-loaded feature maps for drift-free positioning.",
          whyItMatters: "Provides absolute positioning correction for long-range missions without infrastructure.",
          typicalBuyers: ["Defense programs", "Long-range delivery operators"],
          companyIds: ["shield_ai"],
          useCases: [
            {
              id: "long_range_isr",
              label: "Long-Range GPS-Denied ISR",
              description: "Extended range reconnaissance in communications-denied environments.",
              marketStatus: "open",
              useCaseTags: ["isr"],
              domains: ["defense"],
              companyIds: ["shield_ai"],
            },
          ],
        },
      ],
    },

    {
      id: "precision_nav",
      label: "Precision Navigation & Terminal Control",
      description: "High-accuracy positioning and control capabilities for landing, docking, and precision delivery in final approach phases.",
      subCapabilities: [
        {
          id: "autonomous_docking",
          label: "Autonomous Docking",
          description: "Precise alignment and connection to charging pads, docking stations, or vehicle-based platforms.",
          whyItMatters: "Enables persistent autonomous operations without human intervention for charging or payload exchange.",
          typicalBuyers: ["Drone-in-a-box operators", "eVTOL infrastructure developers"],
          companyIds: ["joby_aviation", "beta_technologies"],
          useCases: [
            {
              id: "drone_in_a_box",
              label: "Drone-in-a-Box Systems",
              description: "Fully automated drone deployment, mission, and recharging cycles without operator presence.",
              marketStatus: "crowded",
              useCaseTags: ["inspection", "surveillance"],
              domains: ["industrial"],
              companyIds: ["percepto", "airobotics", "joby_aviation"],
            },
          ],
        },
        {
          id: "precision_landing",
          label: "Precision Landing Systems",
          description: "Centimeter-level accuracy landing on moving or stationary targets using vision, radar, or RF beacons.",
          whyItMatters: "Critical for any autonomous system that must reliably land without a human guiding it.",
          typicalBuyers: ["Delivery operators", "Defense resupply programs", "Industrial drone operators"],
          companyIds: ["dji", "skydio", "percepto", "airobotics", "matternet", "joby_aviation", "beta_technologies"],
          systemCapabilityIds: ["sc_precision_land", "sc_cargo_delivery"],
          useCases: [
            {
              id: "medical_delivery",
              label: "Medical Delivery (Last 50m)",
              description: "Precision delivery of medical supplies to hospital rooftops, disaster zones, or remote clinics.",
              marketStatus: "moderate",
              useCaseTags: ["logistics"],
              domains: ["industrial"],
              companyIds: ["matternet", "zipline"],
            },
            {
              id: "defense_resupply",
              label: "Autonomous Resupply",
              description: "Delivering critical supplies to forward operating bases or troops in contact without risking personnel.",
              marketStatus: "moderate",
              useCaseTags: ["logistics"],
              domains: ["defense"],
              companyIds: ["skydio", "airobotics"],
            },
          ],
        },
        {
          id: "geo_fenced",
          label: "Geo-Fenced Approach Control",
          description: "Automated enforcement of airspace boundaries and approach corridors during terminal flight phases.",
          whyItMatters: "Regulatory compliance and safety — required for BVLOS approvals and urban operations.",
          typicalBuyers: ["Delivery operators", "Urban drone fleet operators"],
          companyIds: ["dji", "percepto", "airobotics", "iris_automation", "wing"],
          useCases: [
            {
              id: "urban_delivery",
              label: "Urban Drone Delivery",
              description: "Package delivery in dense urban environments requiring strict corridor compliance.",
              marketStatus: "crowded",
              useCaseTags: ["logistics"],
              domains: ["industrial"],
              companyIds: ["dji", "wing", "matternet"],
            },
          ],
        },
        {
          id: "rth_failsafe",
          label: "Return-to-Home Failsafe",
          description: "Autonomous recovery navigation when communication is lost or battery is critically low.",
          whyItMatters: "Safety baseline for any commercial drone deployment — reduces loss incidents and liability.",
          typicalBuyers: ["All commercial drone operators"],
          companyIds: ["dji", "airobotics", "zipline"],
          useCases: [
            {
              id: "industrial_monitoring",
              label: "Continuous Industrial Monitoring",
              description: "24/7 automated site inspection with guaranteed safe recovery on any failure mode.",
              marketStatus: "moderate",
              useCaseTags: ["inspection"],
              domains: ["industrial"],
              companyIds: ["percepto", "airobotics"],
            },
          ],
        },
        {
          id: "winch_delivery",
          label: "Winch/Payload Delivery",
          description: "Controlled payload lowering and release for precision delivery without landing.",
          whyItMatters: "Enables delivery to locations where landing is unsafe or impossible — rooftops, ships, tight spaces.",
          typicalBuyers: ["Medical delivery operators", "Military logistics", "Maritime rescue"],
          companyIds: ["zipline", "matternet", "wingcopter"],
          systemCapabilityIds: ["sc_cargo_delivery"],
          useCases: [
            {
              id: "rooftop_landing",
              label: "Rooftop Delivery",
              description: "Cargo delivery to building rooftops in urban areas without landing pad infrastructure.",
              marketStatus: "open",
              useCaseTags: ["logistics"],
              domains: ["industrial"],
              companyIds: ["wingcopter", "matternet"],
            },
          ],
        },
      ],
    },

    {
      id: "swarm_nav",
      label: "Swarm-Relative Navigation",
      description: "Multi-agent positioning and coordination capabilities that enable groups of drones to operate as a coherent system.",
      subCapabilities: [
        {
          id: "rel_positioning",
          label: "Relative Positioning (Inter-Drone)",
          description: "Each drone knows the position of its neighbors without relying on external infrastructure.",
          whyItMatters: "Foundation of all swarm operations — enables formation flight and collision avoidance at scale.",
          typicalBuyers: ["Defense programs", "Agricultural fleet operators"],
          companyIds: ["anduril", "uvision", "shield_ai", "swarming_logic"],
          systemCapabilityIds: ["sc_swarm_coord"],
          useCases: [
            {
              id: "swarm_isr",
              label: "Swarm ISR",
              description: "Coordinated multi-drone intelligence collection covering large areas simultaneously.",
              marketStatus: "crowded",
              useCaseTags: ["isr", "surveillance"],
              domains: ["defense"],
              companyIds: ["anduril", "shield_ai", "uvision"],
            },
          ],
        },
        {
          id: "decentralized_swarm",
          label: "Decentralized Swarm Coordination",
          description: "Swarm behavior emerges from local rules — no single point of failure or control.",
          whyItMatters: "Resilience: loss of individual agents or communications doesn't degrade the entire mission.",
          typicalBuyers: ["Defense integrators", "Research programs"],
          companyIds: ["anduril", "shield_ai", "uvision", "heron_systems", "swarming_logic"],
          systemCapabilityIds: ["sc_swarm_coord", "sc_comms_denied"],
          useCases: [
            {
              id: "cooperative_strike",
              label: "Cooperative Strike",
              description: "Coordinated attack by multiple munitions overwhelming point defenses.",
              marketStatus: "moderate",
              useCaseTags: ["isr"],
              domains: ["defense"],
              companyIds: ["anduril", "uvision", "heron_systems"],
            },
          ],
        },
        {
          id: "leader_follower",
          label: "Leader-Follower Formation",
          description: "Follower drones maintain precise relative position behind a lead vehicle.",
          whyItMatters: "Simplest form of coordination — commercially viable with minimal computational overhead.",
          typicalBuyers: ["Agricultural fleet operators", "Infrastructure inspection providers"],
          companyIds: ["joby_aviation", "heron_systems"],
          useCases: [
            {
              id: "agri_swarm",
              label: "Agricultural Swarm Coverage",
              description: "Coordinated fleet spraying or mapping of large agricultural areas.",
              marketStatus: "open",
              useCaseTags: ["mapping", "inspection"],
              domains: ["industrial"],
              companyIds: ["joby_aviation"],
            },
          ],
        },
        {
          id: "mesh_comms",
          label: "Mesh Communications for Nav",
          description: "Peer-to-peer communication network among swarm agents for position sharing and coordination.",
          whyItMatters: "Eliminates single point of failure in communications — critical for contested environments.",
          typicalBuyers: ["Defense programs"],
          companyIds: ["anduril", "heron_systems", "l3harris"],
          systemCapabilityIds: ["sc_swarm_coord", "sc_comms_denied"],
          useCases: [
            {
              id: "perimeter_surveillance",
              label: "Perimeter Surveillance Mesh",
              description: "Persistent coverage of a large perimeter using coordinated multi-drone patrols.",
              marketStatus: "moderate",
              useCaseTags: ["surveillance"],
              domains: ["defense", "industrial"],
              companyIds: ["anduril", "l3harris"],
            },
          ],
        },
        {
          id: "swarm_collision",
          label: "Collision Avoidance in Swarm",
          description: "Real-time path planning to prevent inter-agent collisions in dense formations.",
          whyItMatters: "Safety and reliability requirement for any commercial or military multi-drone deployment.",
          typicalBuyers: ["All swarm operators"],
          companyIds: ["anduril", "swarming_logic", "l3harris"],
          useCases: [
            {
              id: "sar_swarm",
              label: "Coordinated Search and Rescue",
              description: "Dense multi-drone search patterns in complex terrain or urban environments.",
              marketStatus: "open",
              useCaseTags: ["surveillance"],
              domains: ["defense", "industrial"],
              companyIds: ["swarming_logic"],
            },
          ],
        },
      ],
    },

    {
      id: "payload_positioning",
      label: "Payload-Relative Positioning",
      description: "Precise alignment and stabilization of imaging, sensing, and delivery payloads relative to targets.",
      subCapabilities: [
        {
          id: "eo_gimbal",
          label: "Electro-Optical Gimbal Stabilization",
          description: "Mechanically and electronically stabilizes cameras to eliminate motion blur and maintain target lock.",
          whyItMatters: "Image quality at range depends entirely on stabilization — critical for ISR and precision agriculture.",
          typicalBuyers: ["Defense imagery programs", "Precision agriculture operators", "Broadcast and media"],
          companyIds: ["teledyne_flir", "phase_one", "safran", "shotover", "wescam", "overwatch_imaging", "micasense"],
          systemCapabilityIds: ["sc_target_id", "sc_persistent_isr"],
          useCases: [
            {
              id: "maritime_isr",
              label: "Maritime ISR",
              description: "Wide-area ocean surveillance tracking vessels and submarine activity.",
              marketStatus: "crowded",
              useCaseTags: ["isr", "surveillance"],
              domains: ["defense"],
              companyIds: ["teledyne_flir", "wescam", "overwatch_imaging"],
            },
            {
              id: "agri_mapping",
              label: "Precision Agriculture Mapping",
              description: "High-resolution crop health and field mapping for precision agronomic decisions.",
              marketStatus: "crowded",
              useCaseTags: ["mapping"],
              domains: ["industrial"],
              companyIds: ["micasense", "phase_one", "trimble"],
            },
          ],
        },
        {
          id: "sar_motion",
          label: "SAR Motion Compensation",
          description: "Corrects for platform motion during synthetic aperture radar imaging to produce sharp all-weather imagery.",
          whyItMatters: "SAR is the only sensor that works through clouds and at night — motion compensation is essential for usable imagery.",
          typicalBuyers: ["Defense intelligence programs", "Disaster response agencies"],
          companyIds: ["teledyne_flir", "velodyne", "leica_geosystems", "wescam"],
          systemCapabilityIds: ["sc_sar_collection"],
          useCases: [
            {
              id: "sar_isr",
              label: "All-Weather ISR",
              description: "Intelligence collection unaffected by cloud cover, darkness, or smoke.",
              marketStatus: "moderate",
              useCaseTags: ["isr"],
              domains: ["defense"],
              companyIds: ["teledyne_flir", "wescam"],
            },
          ],
        },
        {
          id: "lidar_coupled",
          label: "LiDAR-Coupled Positioning",
          description: "Combines LiDAR point cloud data with positioning systems for millimeter-accurate 3D mapping.",
          whyItMatters: "Survey-grade 3D models require precise co-registration of LiDAR pulses with aircraft position.",
          typicalBuyers: ["Surveying firms", "Infrastructure asset managers", "Forestry and environmental agencies"],
          companyIds: ["velodyne", "ouster", "trimble", "leica_geosystems", "riegl"],
          useCases: [
            {
              id: "infra_inspection",
              label: "Infrastructure Inspection",
              description: "3D scanning of bridges, power lines, and pipelines for structural assessment.",
              marketStatus: "crowded",
              useCaseTags: ["inspection", "mapping"],
              domains: ["industrial"],
              companyIds: ["velodyne", "trimble", "leica_geosystems", "riegl"],
            },
          ],
        },
        {
          id: "hyperspectral",
          label: "Hyperspectral Alignment",
          description: "Precise spatial alignment of hyperspectral bands to ground truth for accurate spectral analysis.",
          whyItMatters: "Misalignment between spectral bands produces false readings — critical for crop disease detection and mineral mapping.",
          typicalBuyers: ["Agricultural analytics providers", "Environmental monitoring agencies"],
          companyIds: ["micasense", "phase_one", "trimble", "overwatch_imaging"],
          useCases: [
            {
              id: "crop_health",
              label: "Crop Health Monitoring",
              description: "Early disease and stress detection using multispectral drone imagery at field scale.",
              marketStatus: "moderate",
              useCaseTags: ["mapping"],
              domains: ["industrial"],
              companyIds: ["micasense", "trimble"],
            },
          ],
        },
        {
          id: "acoustic_nav",
          label: "Acoustic Positioning (Underwater UAS)",
          description: "Hydroacoustic positioning for underwater drones operating in GPS-denied subsurface environments.",
          whyItMatters: "Only viable positioning approach for underwater unmanned vehicles — opens entire subsea inspection market.",
          typicalBuyers: ["Naval programs", "Offshore energy operators"],
          companyIds: ["advanced_navigation"],
          useCases: [
            {
              id: "subsea_inspection",
              label: "Subsea Infrastructure Inspection",
              description: "Inspection of pipelines, cables, and offshore platforms below the waterline.",
              marketStatus: "open",
              useCaseTags: ["inspection"],
              domains: ["industrial", "defense"],
              companyIds: ["advanced_navigation"],
            },
          ],
        },
      ],
    },

    {
      id: "urban_indoor",
      label: "Urban & Indoor Navigation",
      description: "Positioning and pathfinding capabilities for drones operating inside buildings, tunnels, and dense urban environments.",
      subCapabilities: [
        {
          id: "slam",
          label: "3D SLAM",
          description: "Simultaneously builds a 3D map of the environment while tracking the drone's position within it.",
          whyItMatters: "Enables autonomous navigation without any pre-existing map — critical for post-disaster or unknown environments.",
          typicalBuyers: ["Public safety agencies", "Industrial inspection providers", "Military"],
          companyIds: ["skydio", "percepto", "flyability", "gecko_robotics", "impossible_aerospace", "azur_drones", "brinc_drones"],
          systemCapabilityIds: ["sc_indoor_nav", "sc_perimeter_patrol"],
          useCases: [
            {
              id: "warehouse_inventory",
              label: "Warehouse Inventory Drones",
              description: "Autonomous stocktaking and inventory management inside large distribution facilities.",
              marketStatus: "crowded",
              useCaseTags: ["inspection", "logistics"],
              domains: ["industrial"],
              companyIds: ["skydio", "percepto"],
            },
            {
              id: "disaster_inspection",
              label: "Post-Disaster Building Inspection",
              description: "Assessing structural integrity of buildings after earthquakes or explosions when entry is unsafe.",
              marketStatus: "open",
              useCaseTags: ["inspection"],
              domains: ["industrial", "defense"],
              companyIds: ["flyability", "brinc_drones"],
            },
          ],
        },
        {
          id: "depth_camera",
          label: "Depth-Camera Positioning",
          description: "Stereo or time-of-flight cameras provide dense 3D point clouds for obstacle avoidance and navigation.",
          whyItMatters: "Low-cost alternative to LiDAR for indoor navigation — democratizes autonomous indoor flight.",
          typicalBuyers: ["Consumer drone manufacturers", "Indoor inspection providers"],
          companyIds: ["skydio", "percepto", "flyability", "azur_drones", "brinc_drones", "ceva_inc"],
          useCases: [
            {
              id: "confined_inspection",
              label: "Confined Space Inspection",
              description: "Inspection inside tanks, boilers, and pipelines where humans cannot safely enter.",
              marketStatus: "moderate",
              useCaseTags: ["inspection"],
              domains: ["industrial"],
              companyIds: ["flyability"],
            },
          ],
        },
        {
          id: "floor_plan",
          label: "Floor Plan Anchoring",
          description: "Localizes the drone within a pre-loaded 2D or 3D floor plan for repeatable mission execution.",
          whyItMatters: "Enables precise, repeatable inspection paths across multiple missions in the same facility.",
          typicalBuyers: ["Facility operators", "Security integrators"],
          companyIds: ["flyability", "gecko_robotics"],
          useCases: [
            {
              id: "security_patrol",
              label: "Indoor Security Patrol",
              description: "Scheduled autonomous patrol of facility interiors for intrusion detection.",
              marketStatus: "open",
              useCaseTags: ["surveillance"],
              domains: ["industrial"],
              companyIds: ["azur_drones"],
            },
          ],
        },
        {
          id: "human_safe_corridors",
          label: "Human-Safe Approach Corridors",
          description: "Navigation constraints that guarantee safe separation from humans in shared indoor spaces.",
          whyItMatters: "Regulatory and liability requirement for any drone operating near people — essential for commercial deployment.",
          typicalBuyers: ["Warehouse operators", "Indoor venue operators"],
          companyIds: ["skydio", "impossible_aerospace", "brinc_drones"],
          useCases: [
            {
              id: "swat_recon",
              label: "SWAT Reconnaissance",
              description: "Deploying drones through doorways and corridors to assess threats before officer entry.",
              marketStatus: "moderate",
              useCaseTags: ["surveillance"],
              domains: ["defense"],
              companyIds: ["brinc_drones"],
            },
          ],
        },
        {
          id: "acoustic_indoor",
          label: "Acoustic Nav in GPS-Denied Spaces",
          description: "Uses ultrasonic or acoustic ranging to position drones in environments where optical sensors are impaired.",
          whyItMatters: "Viable positioning fallback in smoke, dust, or total darkness where cameras and LiDAR fail.",
          typicalBuyers: ["Fire services", "Underground mining operators"],
          companyIds: ["flyability"],
          useCases: [
            {
              id: "firefighting_recon",
              label: "Firefighting Reconnaissance",
              description: "Navigating through smoke-filled buildings to locate victims and assess fire spread.",
              marketStatus: "open",
              useCaseTags: ["surveillance", "inspection"],
              domains: ["defense", "industrial"],
              companyIds: ["flyability"],
            },
          ],
        },
      ],
    },

    {
      id: "long_range_nav",
      label: "Long-Range Autonomous Navigation",
      description: "Navigation capabilities that enable drones to operate safely and efficiently over distances of tens to hundreds of kilometers.",
      subCapabilities: [
        {
          id: "bvlos_nav",
          label: "BVLOS Navigation Stack",
          description: "Integrated navigation and communications solution certified for beyond visual line of sight operations.",
          whyItMatters: "The key regulatory and technical unlock for commercial drone delivery and infrastructure inspection at scale.",
          typicalBuyers: ["Cargo delivery operators", "Energy infrastructure operators", "Government border agencies"],
          companyIds: ["zipline", "honeywell_defense", "iris_automation", "l3harris", "general_atomics", "northrop_grumman", "uavionix", "wing", "sabrewing", "garmin_aviation"],
          systemCapabilityIds: ["sc_bvlos"],
          useCases: [
            {
              id: "pipeline_inspection",
              label: "Pipeline Inspection (100+ km)",
              description: "Automated inspection of long-distance oil and gas pipelines for leaks and damage.",
              marketStatus: "crowded",
              useCaseTags: ["inspection"],
              domains: ["industrial"],
              companyIds: ["senseFly", "iris_automation", "uavionix"],
            },
            {
              id: "cargo_delivery",
              label: "Cargo Delivery (Inter-City)",
              description: "Autonomous freight transport between urban centers or to underserved areas.",
              marketStatus: "moderate",
              useCaseTags: ["logistics"],
              domains: ["industrial"],
              companyIds: ["zipline", "wing", "wingcopter", "sabrewing"],
            },
          ],
        },
        {
          id: "daa",
          label: "Detect-and-Avoid (DAA)",
          description: "Onboard sensors and algorithms that detect manned aircraft and other drones, then execute avoidance maneuvers.",
          whyItMatters: "FAA and EASA regulatory requirement for BVLOS approval — the single biggest bottleneck for commercial scaling.",
          typicalBuyers: ["All BVLOS operators", "UAS traffic management providers"],
          companyIds: ["iris_automation", "l3harris", "uavionix", "general_atomics", "northrop_grumman", "garmin_aviation", "wing"],
          systemCapabilityIds: ["sc_bvlos"],
          useCases: [
            {
              id: "border_patrol",
              label: "Border Patrol BVLOS",
              description: "Persistent surveillance of national borders with autonomous conflict avoidance.",
              marketStatus: "moderate",
              useCaseTags: ["surveillance"],
              domains: ["defense"],
              companyIds: ["general_atomics", "l3harris"],
            },
          ],
        },
        {
          id: "terrain_following",
          label: "Low-Altitude Terrain Following",
          description: "Continuously adjusts altitude to maintain a fixed height above varying ground terrain.",
          whyItMatters: "Enables radar-evading low-altitude military flight and consistent sensor coverage for mapping missions.",
          typicalBuyers: ["Defense programs", "Survey operators"],
          companyIds: ["senseFly", "general_atomics", "sabrewing", "garmin_aviation"],
          useCases: [
            {
              id: "wildfire_monitoring",
              label: "Wildfire Perimeter Monitoring",
              description: "Continuous tracking of wildfire boundaries across rugged terrain for resource allocation.",
              marketStatus: "open",
              useCaseTags: ["surveillance", "mapping"],
              domains: ["industrial"],
              companyIds: ["senseFly", "sabrewing"],
            },
          ],
        },
        {
          id: "energy_route",
          label: "Energy-Aware Route Optimization",
          description: "Plans flight paths that balance mission objectives against battery or fuel consumption.",
          whyItMatters: "Range is the primary constraint on drone utility — optimized routing extends effective operational radius by 20-40%.",
          typicalBuyers: ["Delivery operators", "Survey operators"],
          companyIds: ["zipline", "northrop_grumman", "senseFly", "wingcopter"],
          useCases: [
            {
              id: "maritime_surveillance",
              label: "Maritime Surveillance",
              description: "Wide-area ocean monitoring covering large patrol zones with minimal energy expenditure.",
              marketStatus: "moderate",
              useCaseTags: ["surveillance"],
              domains: ["defense"],
              companyIds: ["northrop_grumman", "general_atomics"],
            },
          ],
        },
        {
          id: "comms_resilient",
          label: "Comms-Resilient Autonomous Nav",
          description: "Maintains mission execution and safe flight when the communication link to the ground station is lost.",
          whyItMatters: "Communications loss is inevitable over long ranges — autonomous contingency behavior is a safety and mission requirement.",
          typicalBuyers: ["Defense programs", "Remote area operators"],
          companyIds: ["general_atomics", "northrop_grumman", "sabrewing"],
          systemCapabilityIds: ["sc_comms_denied"],
          useCases: [
            {
              id: "comms_denied_isr",
              label: "Comms-Denied ISR",
              description: "Executing pre-planned ISR missions in environments where adversary jamming disrupts communications.",
              marketStatus: "moderate",
              useCaseTags: ["isr"],
              domains: ["defense"],
              companyIds: ["general_atomics", "northrop_grumman"],
            },
          ],
        },
      ],
    },
  ],
};
